import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getProductsForRegion, isEdBuyRouted } from "../lib/products";
import { useCart } from "../lib/cartStore";
import { loadRegionChoice, saveRegionChoice, type RegionChoice } from "../lib/regionStore";
import { ProductCard } from "../components/ProductCard";
import { CartSidebar } from "../components/CartSidebar";
import { SubjectSection } from "../components/SubjectSection";
import { RegionSelector } from "../components/RegionSelector";
import { EdBuyInstructions } from "../components/EdBuyInstructions";

export function DirectOrderPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isEmpty, entries, getTotals } = useCart();

  // Region is a display filter only: changing it never touches the cart, and
  // checkout still accepts any school. Persisted so /checkout → back restores the view.
  const [regionChoice, setRegionChoice] = useState<RegionChoice>(loadRegionChoice);
  const { region, schoolType } = regionChoice;

  function updateRegionChoice(next: RegionChoice) {
    setRegionChoice(next);
    saveRegionChoice(next);
  }

  const productsBySubject = getProductsForRegion(region, schoolType);
  // NSW & ACT government schools order through EdBuy: instructions replace the picker
  // and the cart column. The cart itself lives in context/sessionStorage and is kept.
  const showEdBuy = isEdBuyRouted(region, schoolType);

  // Navigate to checkout page with cart data
  function handleCheckout() {
    if (isEmpty) return;

    setSubmitting(true);
    setError(null);

    // Pass cart data to checkout page
    navigate("/checkout", {
      state: {
        entries,
        totals: getTotals(),
      },
    });
  }

  return (
    <div className="order-page">
      <header className="order-header order-header--direct">
        <div className="order-header-brand">
          <img src="https://smartered.wpengine.com/wp-content/uploads/2023/01/SmarterEd-logo.svg" alt="SmarterEd" className="order-header-logo" />
        </div>
        <div className="order-header-text">
          <h1>Choose Your SmarterEd Products</h1>
          <p>Select the courses you’d like to include in your order</p>
        </div>
      </header>

      {error && <div className="error-banner">{error}</div>}

      <div className="order-region">
        <RegionSelector
          region={region}
          schoolType={schoolType}
          onRegionChange={(next) => updateRegionChoice({ region: next, schoolType })}
          onSchoolTypeChange={(next) => updateRegionChoice({ region, schoolType: next })}
        />
        {showEdBuy ? <EdBuyInstructions /> : <h2 className="order-step-heading">Choose your products</h2>}
      </div>

      {!showEdBuy && (
        <div className="order-layout order-layout--direct">
          <main className="catalog-section">
            {Array.from(productsBySubject.entries()).map(([subject, products]) => (
              <SubjectSection key={subject.id} subject={subject} productIds={products.map((group) => group.id)}>
                <div className="product-grid">
                  {products.map((group) => (
                    <ProductCard key={group.id} group={group} />
                  ))}
                </div>
              </SubjectSection>
            ))}
          </main>

          <aside className="cart-section">
            <CartSidebar
              onSubmit={handleCheckout}
              onCancel={() => {}} // No cancel for direct orders
              submitting={submitting}
              isPunchOut={false}
            />
          </aside>
        </div>
      )}

      <footer className="order-footer">
        <div className="order-footer-inner">
          <p className="order-footer-help">
            <svg className="order-footer-help-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="28" height="28">
              <circle cx="12" cy="12" r="10.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
              <path d="M9.2 9.3a2.9 2.9 0 0 1 5.6 1c0 1.9-2.8 2.4-2.8 4.1" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              <circle cx="12" cy="17.6" r="1" fill="currentColor" />
            </svg>
            <span>
              Need help? Call <a href="tel:1800371077" className="order-footer-phone">1800 371 077</a> or email{" "}
              <a href="mailto:accounts@smartered.com.au" className="order-footer-email">accounts@smartered.com.au</a>
            </span>
          </p>
          <p className="order-footer-trust">Trusted by over 700 NSW high schools, and many more around Australia.</p>
        </div>
      </footer>
    </div>
  );
}
